"use client"

import { Plus, Trash01 } from "@untitledui/icons"

import { Button } from "@/components/ui/button"
import { ButtonUtility } from "@/components/ui/button-utility"
import { Input } from "@/components/ui/input"
import { Spinner } from "@/components/ui/spinner"
import { useUrlLookup } from "@/hooks/use-url-lookup"
import { isUsable } from "@/lib/content-lookup"

export interface SeedRow {
  _key: string
  url: string
  notes: string
  hint: string
}

// Stable client-only keys so React and the field-note resolver can track rows
// across add/remove. The server maps `_key` to the materialised post.
let seedRowCounter = 0
export const newSeedRow = (): SeedRow => ({
  _key: `row-${(seedRowCounter += 1)}`,
  url: "",
  notes: "",
  hint: "",
})

interface SeedRowsProps {
  rows: SeedRow[]
  onChange: (rows: SeedRow[]) => void
}

export function SeedRows({ rows, onChange }: SeedRowsProps) {
  const update = (key: string, patch: Partial<SeedRow>) =>
    onChange(rows.map((row) => (row._key === key ? { ...row, ...patch } : row)))
  const remove = (key: string) =>
    onChange(rows.filter((row) => row._key !== key))
  const add = () => onChange([...rows, newSeedRow()])

  return (
    <div className="flex flex-col gap-4">
      {rows.map((row, index) => (
        <SeedRowCard
          key={row._key}
          row={row}
          index={index}
          canRemove={rows.length > 1}
          onUpdate={(patch) => update(row._key, patch)}
          onRemove={() => remove(row._key)}
        />
      ))}

      <div>
        <Button color="secondary" size="sm" iconLeading={Plus} onClick={add}>
          Add track
        </Button>
      </div>
    </div>
  )
}

interface SeedRowCardProps {
  row: SeedRow
  index: number
  canRemove: boolean
  onUpdate: (patch: Partial<SeedRow>) => void
  onRemove: () => void
}

function SeedRowCard({
  row,
  index,
  canRemove,
  onUpdate,
  onRemove,
}: SeedRowCardProps) {
  // Live, preview-only lookup so the admin sees what they're seeding before
  // committing. It never touches persistence: CreateService mints the Post
  // from offline detection inside its transaction (see create_service.rb),
  // so this is purely a confidence check on the paste.
  const { result, loading, failed } = useUrlLookup(row.url)

  return (
    <div className="border-secondary bg-secondary flex flex-col gap-3 rounded-xl border p-4">
      <div className="flex items-center justify-between">
        <span className="text-tertiary text-xs font-semibold tracking-wide uppercase">
          Track {index + 1}
        </span>
        {canRemove && (
          <ButtonUtility
            size="xs"
            color="tertiary"
            tooltip="Remove track"
            icon={Trash01}
            onClick={onRemove}
          />
        )}
      </div>

      <Input
        label="Link"
        placeholder="https://…"
        value={row.url}
        onChange={(value) => onUpdate({ url: value })}
      />

      <SeedRowPreview loading={loading} failed={failed} result={result} />

      <Input
        label="Note"
        hint="Public — ships on the published playlist."
        placeholder="Why this one matters"
        value={row.notes}
        onChange={(value) => onUpdate({ notes: value })}
      />
      <Input
        label="Margin note"
        hint="Private — only your invitee sees this, and it disappears once they change the track."
        placeholder="e.g. swap this if the live version isn’t the one you meant"
        value={row.hint}
        onChange={(value) => onUpdate({ hint: value })}
      />
    </div>
  )
}

const TYPE_LABEL: Record<string, string> = {
  VideoBody: "Video",
  AudioBody: "Audio",
  ArticleBody: "Article",
  PhotoBody: "Photo",
}

function SeedRowPreview({
  loading,
  failed,
  result,
}: {
  loading: boolean
  failed: boolean
  result: ReturnType<typeof useUrlLookup>["result"]
}) {
  if (loading) {
    return (
      <p className="text-tertiary flex items-center gap-2 text-sm">
        <Spinner className="size-4" />
        Looking up this link…
      </p>
    )
  }

  if (isUsable(result)) {
    const detail =
      result.source ?? TYPE_LABEL[result.content_body_type ?? ""] ?? null
    return (
      <div className="border-secondary bg-primary flex items-center gap-3 rounded-lg border p-2">
        {result.fields.thumbnail_url && (
          <img
            src={result.fields.thumbnail_url}
            alt=""
            className="size-10 shrink-0 rounded object-cover"
          />
        )}
        <div className="flex min-w-0 flex-col">
          <span className="text-secondary truncate text-sm font-medium">
            {result.fields.title}
          </span>
          {detail && <span className="text-tertiary text-xs">{detail}</span>}
        </div>
      </div>
    )
  }

  // Unreachable / invalid / network failure — informational only, since the
  // row still seeds from offline detection server-side.
  if (failed || result) {
    return (
      <p className="text-tertiary text-sm">
        We couldn&apos;t preview this link — it&apos;ll still seed from the URL.
      </p>
    )
  }

  return null
}
